import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Timestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAllUsersInTimeWindow, getUserFromTimeBasedCollection, saveMessageToCollectionSentMessages } from '../utils/firebaseUtils';
import { DUMMY_NICKNAMES, DUMMY_BACKGROUNDS, getRandomDummyNickname, getRandomDummyBackground } from '../utils/dummyData';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  font-family: 'MapoDPP', sans-serif;
  color: white;
  overflow-y: auto;
  padding: 2rem;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
`;

const ControlPanel = styled.div`
  width: 100%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  width: 120px;
  text-align: center;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #4CAF50;
          color: white;
          &:hover {
            background: #45a049;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: #2196F3;
          color: white;
          &:hover {
            background: #1976D2;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
          }
        `;
      case 'danger':
        return `
          background: #f44336;
          color: white;
          &:hover {
            background: #d32f2f;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
          }
        `;
      default:
        return `
          background: #666;
          color: white;
          &:hover {
            background: #555;
            transform: translateY(-2px);
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
`;

const StatusPanel = styled.div`
  width: 100%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
`;

const StatusText = styled.div<{ type?: 'success' | 'error' | 'info' }>`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border-radius: 5px;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          background: rgba(76, 175, 80, 0.2);
          color: #4CAF50;
          border-left: 4px solid #4CAF50;
        `;
      case 'error':
        return `
          background: rgba(244, 67, 54, 0.2);
          color: #f44336;
          border-left: 4px solid #f44336;
        `;
      case 'info':
        return `
          background: rgba(33, 150, 243, 0.2);
          color: #2196F3;
          border-left: 4px solid #2196F3;
        `;
      default:
        return `
          background: rgba(255, 255, 255, 0.1);
          color: white;
        `;
    }
  }}
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  overflow: hidden;
  margin: 1rem 0;
  
  &::after {
    content: '';
    display: block;
    width: ${props => props.progress}%;
    height: 100%;
    background: linear-gradient(90deg, #4CAF50, #45a049);
    transition: width 0.3s ease;
  }
`;

const UserList = styled.div`
  width: 100%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const UserItem = styled.div<{ isSelected?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  background: ${props => props.isSelected ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  border-left: 4px solid ${props => props.isSelected ? '#4CAF50' : '#666'};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.isSelected ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-1px);
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserNickname = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.2rem;
`;

const UserBackground = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const MessageCount = styled.div`
  font-size: 0.9rem;
  color: #4CAF50;
  font-weight: 600;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-right: 1rem;
  cursor: pointer;
  accent-color: #4CAF50;
`;

const SelectionControls = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const SelectionInfo = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: #4CAF50;
  font-weight: 600;
`;

// 미리 작성된 메시지 목록
const predefinedMessages: string[] = [
  '"당신의 선택에 흥미로워합니다."',
  '"당신의 선택에 재미있어합니다."',
  '"당신의 호쾌한 발언에\n고개를 끄덕입니다."',
  '"당신의 선택에 고개를 끄덕입니다."',
  '"당신의 계획에 눈을 반짝입니다."',
  '"당신의 계책을 궁금해합니다."',
  '"당신의 근성에 감탄합니다."',
  '"당신의 침착함에 감탄합니다."',
  '"알고 있었다는 듯\n흐뭇한 미소를 짓습니다."',
  '"당신의 준비성에 감탄합니다."',
  '"즐거워합니다."',
  '"당신의 혼잣말을 좋아합니다."',
  '"눈을 반짝입니다."',
  '"당신의 선택을 기대합니다."',
  '"감동하며 눈물을 글썽입니다."',
  '"아쉬워합니다."',
  '"기뻐합니다."',
  '"당신의 준비성을 칭찬합니다."',
  '"그럴줄 알았다며 감탄합니다."',
];

interface UserData {
  id: string;
  nickname: string;
  background: string;
  createdAt: Date | Timestamp;
  messageCount?: number;
  isSelected?: boolean;
}

const BulkMessageTestPage: React.FC = () => {
  const [messageCount, setMessageCount] = useState<number>(10);
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [sentCount, setSentCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  // 사용자 목록 가져오기
  const fetchUsers = async () => {
    setIsLoading(true);
    setStatus('사용자 목록을 가져오는 중...');
    
    try {
      const allUsers = await getAllUsersInTimeWindow(2); // 2시간 이내
      
      // CollectionSentMessages 문서 제외하고 필터링
      const filteredUsers = allUsers
        .filter(user => !user.id.includes('CollectionSentMessages'))
        .map(user => ({
          id: user.id,
          nickname: user.nickname || '익명',
          background: user.background || '없음',
          createdAt: user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt),
          messageCount: 0,
          isSelected: false
        }));

      setUsers(filteredUsers);
      setStatus(`총 ${filteredUsers.length}명의 사용자를 찾았습니다.`);
    } catch (error) {
      console.error('사용자 목록 가져오기 오류:', error);
      setStatus('사용자 목록을 가져오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 사용자 선택/해제 함수
  const toggleUserSelection = (userId: string) => {
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === userId 
          ? { ...user, isSelected: !user.isSelected }
          : user
      )
    );
  };

  // 전체 선택/해제 함수
  const toggleAllUsers = () => {
    const allSelected = users.every(user => user.isSelected);
    setUsers(prevUsers => 
      prevUsers.map(user => ({ ...user, isSelected: !allSelected }))
    );
  };

  // 선택된 사용자 수 계산
  const selectedUsersCount = users.filter(user => user.isSelected).length;

  // 메시지 전송 함수
  const sendBulkMessages = async () => {
    const selectedUsers = users.filter(user => user.isSelected);
    
    if (selectedUsers.length === 0) {
      setStatus('전송할 사용자를 선택해주세요.');
      return;
    }

    setIsSending(true);
    setProgress(0);
    setSentCount(0);
    setTotalCount(selectedUsers.length * messageCount);
    setStatus('메시지 전송을 시작합니다...');

    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < selectedUsers.length; i++) {
        const user = selectedUsers[i];
        setStatus(`${user.nickname}에게 메시지 전송 중... (${i + 1}/${selectedUsers.length})`);

        // 각 사용자에게 지정된 개수만큼 메시지 전송
        for (let j = 0; j < messageCount; j++) {
          try {
            // 랜덤한 발신자 선택 (더미 데이터에서)
            const senderNickname = getRandomDummyNickname();
            const senderBackground = getRandomDummyBackground();
            
            // 랜덤한 메시지 선택
            const randomMessage = predefinedMessages[Math.floor(Math.random() * predefinedMessages.length)];
            
            // 메시지 전송
            const success = await sendMessageToUser(user.id, randomMessage, senderNickname);
            
            if (success) {
              successCount++;
            } else {
              errorCount++;
            }
            
            // 진행률 업데이트
            const currentProgress = ((i * messageCount + j + 1) / totalCount) * 100;
            setProgress(currentProgress);
            setSentCount(i * messageCount + j + 1);
            
            // 메시지 간 간격 (MessageReceiver가 각 메시지를 개별적으로 감지할 수 있도록 충분한 간격)
            await new Promise(resolve => setTimeout(resolve, 2000)); // 2초 간격
            
          } catch (error) {
            console.error(`메시지 전송 오류 (${user.nickname}, ${j + 1}번째):`, error);
            errorCount++;
          }
        }
      }

      setStatus(`전송 완료! 성공: ${successCount}개, 실패: ${errorCount}개`);
      setProgress(100);
      
    } catch (error) {
      console.error('대량 메시지 전송 오류:', error);
      setStatus('메시지 전송 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  // 개별 메시지 전송 함수
  const sendMessageToUser = async (receiverId: string, message: string, senderNickname: string): Promise<boolean> => {
    try {
      // receiverId 파싱 (users_YYYYMMDD_HH_docId 형태)
      const parts = receiverId.split('_');
      if (parts.length < 4) {
        console.error('잘못된 receiverId 형식:', receiverId);
        return false;
      }
      
      const collectionName = `${parts[0]}_${parts[1]}_${parts[2]}`;
      const docId = parts[3];
      
      const userDocRef = doc(db, collectionName, docId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        const newMessage = {
          message: message,
          senderNickname: senderNickname,
          receivedAt: Timestamp.now()
        };
        
        // 기존 receivedMessages 배열에 추가
        const updatedMessages = userData.receivedMessages 
          ? [...userData.receivedMessages, newMessage]
          : [newMessage];
        
        await updateDoc(userDocRef, {
          receivedMessages: updatedMessages
        });
        
        // CollectionSentMessages에도 저장
        const receiverNickname = userData.nickname || '익명';
        const senderAvatarNumber = '00'; // 테스트용 기본값
        
        await saveMessageToCollectionSentMessages(
          collectionName,
          message,
          receiverNickname,
          senderNickname,
          senderAvatarNumber
        );
        
        return true;
      } else {
        console.error('수신자 문서를 찾을 수 없습니다:', receiverId);
        return false;
      }
    } catch (error) {
      console.error('메시지 전송 오류:', error);
      return false;
    }
  };

  // 컴포넌트 마운트 시 사용자 목록 가져오기
  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <Container>
      <Title>메시지 대량 전송 테스트</Title>
      
      <ControlPanel>
        <ControlRow>
          <Label>메시지 개수 (사용자당):</Label>
          <Input
            type="number"
            min="1"
            max="50"
            value={messageCount}
            onChange={(e) => setMessageCount(parseInt(e.target.value) || 10)}
            disabled={isSending}
          />
        </ControlRow>
        
        <ControlRow>
          <Button 
            onClick={fetchUsers} 
            variant="secondary"
            disabled={isLoading || isSending}
          >
            {isLoading ? '로딩 중...' : '사용자 목록 새로고침'}
          </Button>
          
          <Button 
            onClick={sendBulkMessages} 
            variant="primary"
            disabled={isLoading || isSending || selectedUsersCount === 0}
          >
            {isSending ? '전송 중...' : '대량 전송 시작'}
          </Button>
        </ControlRow>
      </ControlPanel>

      <StatusPanel>
        <StatusText type="info">
          현재 상태: {status}
        </StatusText>
        
        {isSending && (
          <>
            <StatusText>
              진행률: {sentCount} / {totalCount} ({Math.round(progress)}%)
            </StatusText>
            <ProgressBar progress={progress} />
          </>
        )}
        
        {selectedUsersCount > 0 && (
          <StatusText type="success">
            선택된 {selectedUsersCount}명의 사용자에게 각각 {messageCount}개씩 메시지를 전송합니다.
            (총 {selectedUsersCount * messageCount}개)
          </StatusText>
        )}
      </StatusPanel>

      <UserList>
        <h3 style={{ marginBottom: '1rem', textAlign: 'center' }}>사용자 목록</h3>
        
        {users.length > 0 && (
          <>
            <SelectionInfo>
              선택된 사용자: {selectedUsersCount}명 / 전체 {users.length}명
            </SelectionInfo>
            
            <SelectionControls>
              <Button 
                onClick={toggleAllUsers} 
                variant="secondary"
                disabled={isSending}
              >
                {users.every(user => user.isSelected) ? '전체 해제' : '전체 선택'}
              </Button>
            </SelectionControls>
          </>
        )}
        
        {users.length > 0 ? (
          users.map((user) => (
            <UserItem 
              key={user.id} 
              isSelected={user.isSelected}
              onClick={() => toggleUserSelection(user.id)}
            >
              <Checkbox
                type="checkbox"
                checked={user.isSelected}
                onChange={() => toggleUserSelection(user.id)}
                disabled={isSending}
              />
              <UserInfo>
                <UserNickname>{user.nickname}</UserNickname>
                <UserBackground>{user.background}</UserBackground>
              </UserInfo>
              <MessageCount>
                {user.isSelected ? `${messageCount}개 전송 예정` : '선택되지 않음'}
              </MessageCount>
            </UserItem>
          ))
        ) : (
          <StatusText type="info">
            사용자가 없습니다. 새로고침 버튼을 눌러 사용자 목록을 가져오세요.
          </StatusText>
        )}
      </UserList>
    </Container>
  );
};

export default BulkMessageTestPage;
